import React, { useEffect, useState } from "react";
import CustomButton from "../CustomButton";
import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import ModalLayout from "../layouts/ModalLayout";
import { useLocation, useNavigate } from "react-router-dom";
import CalendarAndEventApis from "../../api/CalendarAndEventApis";
import { toast } from "sonner";
import { useBranchCode } from "../../util/hooks/useBranch";
import { EventData } from "../../types/eventType";
import useConfirmToast from "../../util/hooks/useConfirmToast";

interface Props {
  onClose?: () => void;
  onSuccess?: () => void;
  selectedDate?: string;
  mode?: "create" | "edit";
}

function CreateEvent({
  onClose,
  onSuccess,
  selectedDate,
  mode: propMode,
}: Props) {
  const location = useLocation();

  const mode: "create" | "edit" = propMode ?? location.state?.mode ?? "create";
  const existingEvent: EventData | undefined = location.state?.event;
  const navigate = useNavigate();

  const branchCode = useBranchCode();
  const { modal, confirm } = useConfirmToast();

  const [formData, setFormData] = useState({
    occasionType: "",
    eventType: "NORMAL",
    appliesTo: "STUDENT",
    startDate: selectedDate || "",
    startTime: "",
    title: "",
    description: "",
    isRecurring: true,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (mode === "edit" && existingEvent) {
      setFormData({
        occasionType: existingEvent.occasionType,
        eventType: existingEvent.eventType,
        appliesTo: existingEvent.appliesTo,
        startDate: existingEvent.startDate,
        startTime: existingEvent.startTime || "",
        title: existingEvent.title,
        description: existingEvent.description,
        isRecurring: existingEvent.isRecurring ?? false,
        // recurrencePattern: existingEvent.recurrencePattern || "",
      });
    }
  }, [mode, existingEvent]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!branchCode) {
      toast.error("Branch not selected");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        ...formData,
        startTime: formData.startTime || null,
        branchCode,
      };

      let response;

      if (mode === "edit" && existingEvent?.eventId) {
        // edit
        response = await CalendarAndEventApis.updateEvent(
          existingEvent.eventId,
          payload,
        );
      } else {
        // Post
        response = await CalendarAndEventApis.createEvent(payload);
      }

      if (response?.status === 1) {
        toast.success(
          `Event ${mode === "edit" ? "updated" : "created"} successfully`,
        );

        if (location.state?.mode === "edit") {
          navigate("/calendar-and-events", { state: { refresh: true } });
        } else {
          onSuccess?.();
          onClose?.();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(`Failed to ${mode} event`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (eventId: number) => {
    const confirmDelete = await confirm(
      "Are you sure you want to delete this event?",
    );

    if (!confirmDelete) return;

    const response = await CalendarAndEventApis.deleteEvent(eventId);

    if (response?.status === 1) {
      toast.success("Event deleted successfully");
      onSuccess?.();
      onClose();

      navigate("/calendar-and-events", { state: { refresh: true } });
    }
  };

  return (
    <ModalLayout onClose={onClose} width="max-w-[800px]">
      <h4 className="text-xl font-semibold text-neutral-400 mb-4">
        {mode === "create" ? "Create Event" : "View Event"}
      </h4>

      <form
        className="flex flex-col relative z-[80] gap-4"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-6">
          {/* Row 1 */}
          <div className="flex flex-col lg:flex-row gap-4 w-full">
            <CustomSelect
              name="occasionType"
              label="Occasion Type"
              value={formData.occasionType}
              options={[
                "EXAM",
                "MEETING",
                "FESTIVAL",
                "SPORTS",
                "HOLIDAY",
                "OTHER",
              ]}
              placeholder="Select occasion type"
              onChange={handleInputChange}
              selectClassName="w-full"
            />

            <div className="w-full">
              <CustomInput
                name="startDate"
                label="Date"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="w-full">
              <CustomInput
                name="startTime"
                label="Time"
                type="time"
                value={formData.startTime}
                onChange={handleInputChange}
                inputClassName="w-full"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-2 gap-7">
            <CustomSelect
              name="eventType"
              label="Event Type"
              value={formData.eventType}
              options={["NORMAL", "HOLIDAY"]}
              placeholder="Select event type"
              onChange={handleInputChange}
              selectClassName="w-full"
            />

            <CustomSelect
              name="appliesTo"
              label="Applies To"
              value={formData.appliesTo}
              options={["STUDENT", "STAFF", "BOTH"]}
              placeholder="Select target"
              onChange={handleInputChange}
              selectClassName="w-full"
            />

            {/* <CustomSelect
              name="recurrencePattern"
              label="Reminder Preference"
              value={formData.recurrencePattern}
              options={["NONE", "DAILY", "WEEKLY", "MONTHLY", "YEARLY"]}
              placeholder="Select reminder preference"
              onChange={(e) => {
                const value = e.target.value;

                setFormData((prev) => ({
                  ...prev,
                  recurrencePattern: value === "NONE" ? "" : value,
                  isRecurring: value !== "NONE",
                }));
              }}
              selectClassName="w-full"
            /> */}
          </div>

          {/* Title */}
          <CustomInput
            name="title"
            label="Title"
            placeholder="Enter the title of your event"
            value={formData.title}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          {/* Description */}
          <CustomInput
            name="description"
            label="Message"
            placeholder="Describe your event"
            isTextarea
            value={formData.description}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          {mode === "create" ? (
            <>
              <CustomButton
                color="bg-neutral-200"
                text="Cancel"
                onClick={onClose}
                type="button"
              />
              <CustomButton
                color="bg-primary-500"
                text={isLoading ? "Saving..." : "Save"}
                type="submit"
                disabled={isLoading}
              />
            </>
          ) : (
            <>
              <CustomButton
                color="bg-error-500"
                text="Delete"
                onClick={() => {
                  if (existingEvent?.eventId) {
                    handleDelete(existingEvent.eventId);
                  }
                }}
                type="button"
              />
              <CustomButton
                color={mode === "edit" ? "bg-success-600" : "bg-primary-500"}
                text={
                  isLoading
                    ? "Saving..."
                    : mode === "edit"
                      ? "Update Event"
                      : "Save Event"
                }
                type="submit"
                disabled={isLoading}
              />
            </>
          )}
        </div>
        {modal}
      </form>
    </ModalLayout>
  );
}

export default CreateEvent;
